import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Download, Users } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import BeltBar, { BELT_LABELS, degreeLabel } from "@/components/BeltBar";
import { authApi, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] outline-none transition-colors duration-200";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

export default function Certificados() {
  const { user } = useAppUser() || {};
  const [alunos, setAlunos] = useState(null);
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [emitting, setEmitting] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!user) return;
    authApi().get("/alunos").then((r) => setAlunos(r.data.alunos)).catch(() => setAlunos([]));
  }, [user]);

  const loadGraduacoes = async (em) => {
    setEmail(em);
    setData(null);
    if (!em) return;
    try {
      const r = await authApi().get(`/alunos/${encodeURIComponent(em)}/graduacoes`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const emit = async (g) => {
    setEmitting(g.id);
    try {
      const r = await authApi().post("/certificados", { user_email: email, graduation_id: g.id });
      toast.success(r.data.already ? "Este certificado já havia sido emitido." : "Certificado emitido. O aluno já pode baixar na área dele.");
      await loadGraduacoes(email);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEmitting(null);
    }
  };

  const download = async (cert) => {
    setDownloading(cert.id);
    try {
      const r = await authApi().get(`/certificados/${cert.id}/arquivo`);
      const a = document.createElement("a");
      a.href = r.data.data_base64;
      a.download = r.data.file_name;
      a.click();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <AppShell active="certificados">
      <section data-testid="certificados-page">
        <h1 className="font-display text-2xl font-bold" data-testid="certificados-title">Certificados</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6">EMISSÃO DE CERTIFICADO DE GRADUAÇÃO</p>

        <div className="bg-surface border border-white/10 rounded-2xl p-6" data-testid="certificados-select-card">
          <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">SELECIONE O ALUNO</p>
          <select value={email} onChange={(e) => loadGraduacoes(e.target.value)} className={inputCls} data-testid="certificados-aluno-select">
            <option value="">Escolha um aluno...</option>
            {(alunos || []).map((a) => (
              <option key={a.email} value={a.email}>
                {a.name}{a.unit_name ? ` — ${a.unit_name}` : ""}
              </option>
            ))}
          </select>
          {alunos !== null && alunos.length === 0 && (
            <p className="text-sm text-zinc-500 mt-3" data-testid="certificados-no-alunos">Nenhum aluno visível para o seu perfil.</p>
          )}
        </div>

        {email && data === null && <p className="text-zinc-500 text-sm mt-6">Carregando histórico...</p>}

        {data && (
          <div className="mt-6 bg-[#1C1C23] border border-white/10 rounded-2xl p-6" data-testid="certificados-history-card">
            <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5" data-testid="certificados-history-label">
              HISTÓRICO DE GRADUAÇÕES — {(data.aluno.name || "").toUpperCase()} ({data.graduacoes.length})
            </p>
            {data.graduacoes.length === 0 ? (
              <div className="flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-12 text-zinc-500" data-testid="certificados-empty">
                <Users size={28} />
                <p className="text-sm">Nenhuma graduação registrada para este aluno.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.graduacoes.map((g, i) => (
                  <div key={g.id} className="flex items-center gap-4 flex-wrap border border-white/10 rounded-xl px-4 py-3.5 bg-obsidian" data-testid={`certificados-row-${i}`}>
                    <BeltBar belt={g.belt} degree={g.degree} size="sm" testid={`certificados-belt-${i}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">
                        Faixa {BELT_LABELS[g.belt]} · {degreeLabel(g.degree)}
                      </p>
                      <p className="font-mono text-[11px] text-zinc-500 mt-0.5">
                        {fmtDate(g.created_at)} · por {g.granted_by}
                      </p>
                    </div>
                    {g.certificate ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-green-500/50 text-green-400 bg-green-500/10" data-testid={`certificados-emitido-${i}`}>
                          EMITIDO
                        </span>
                        <button
                          type="button"
                          onClick={() => download(g.certificate)}
                          disabled={downloading === g.certificate.id}
                          data-testid={`certificados-download-${i}`}
                          className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-3 py-2 rounded-lg hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-50"
                        >
                          <Download size={13} /> {downloading === g.certificate.id ? "..." : "BAIXAR"}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => emit(g)}
                        disabled={emitting === g.id}
                        data-testid={`certificados-emit-${i}`}
                        className="inline-flex items-center gap-1.5 font-display font-semibold text-xs bg-gold text-obsidian px-4 py-2 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-50 shrink-0"
                      >
                        <Award size={14} /> {emitting === g.id ? "Emitindo..." : "Emitir Certificado"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </AppShell>
  );
}
